import {
  PaginationState, createColumnHelper, flexRender,
  getCoreRowModel, getSortedRowModel, SortingState, useReactTable,
} from "@tanstack/react-table";
import DivLoader from "components/divloader/DivLoader";
import React, { useEffect, useState } from "react";
import Pagination from "components/pagination";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";
import { PayoutRowObj } from "..";
import moment from "moment";
import ShortTruncateCopy from "components/common/ShortTruncateCopy";
import { AiFillMinusCircle, AiFillPlusCircle } from "react-icons/ai";
import Searchbox from "components/fields/Searchbox";
import StatusRender from "./StatusRender";
import InfoModal from "./infoModal";
import CustomToolTip from "components/tooltips/new";
import { getImage } from "utils/commonFunction";
import Card from "components/card";
import { FP_TABLE_STYLES } from "./PaymentsList";
import AddBatchFile from "./AddBatchFile";

const columnHelper = createColumnHelper<PayoutRowObj>();

function PayoutList(props: {
  tableData: any; fetchPayouts: () => void; onValueChange: (value: string) => void;
  roleData?: any; isLoading: boolean; page: number; setPage: any;
  totalpage: number; totalItems: number; currentPage: number;
  pageSize: number; setPageSize: any; filterData: () => void;
  filter: any; setFilter: (e: any) => void;
  setLocalFilters?: (e: any) => void; setClearClick: () => void; localFilters?: any;
}) {
  const { tableData, onValueChange, isLoading, roleData, page, setPage,
    totalpage, totalItems, currentPage, pageSize, setPageSize, fetchPayouts } = props;

  const [{ pageIndex }, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize });
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = React.useState(() => [...(tableData ?? [])]);
  const pagination = React.useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);

  React.useEffect(() => { setExpandedRow(null); }, [page, pageSize]);
  useEffect(() => {
    setData(tableData?.map((item: any) => ({ ...item })) ?? []);
  }, [tableData]);

  const handleExpand = (rowId: any) => setExpandedRow(expandedRow === rowId ? null : rowId);
  const approveBatch = (id: any) => {};

  const buildCols = (withAction: boolean) => [
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: () => <span className="fp-th">Created Date</span>,
      cell: (info: any) => (
        <button className="fp-expand-cell" onClick={() => handleExpand(info?.row?.original?.id)}>
          {expandedRow === info?.row?.original?.id
            ? <AiFillMinusCircle className="fp-expand-icon fp-expand-icon--minus" />
            : <AiFillPlusCircle className="fp-expand-icon fp-expand-icon--plus" />}
          <span className="fp-date">{moment(info.getValue()).format("YYYY-MM-DD HH:mm:ss")}</span>
        </button>
      ),
      size: 200,
    }),
    columnHelper.accessor("batchId", {
      id: "batchId",
      header: () => <span className="fp-th">Batch ID</span>,
      cell: (info) => <ShortTruncateCopy info={info?.getValue()} showCopy />,
    }),
    columnHelper.accessor("clientName", {
      id: "clientName",
      header: () => <span className="fp-th">Client</span>,
      cell: (info) => (
        <div className="fp-client-cell">
          <div className="fp-client-avatar">{info.getValue()?.charAt(0)?.toUpperCase() || "?"}</div>
          <span className="fp-client-name">{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor("totalTransactions", {
      id: "totalTransactions",
      header: () => <span className="fp-th">TX #</span>,
      cell: (info: any) => <span className="fp-num">{info.getValue()}</span>,
    }),
    columnHelper.accessor("gatewayName", {
      id: "gatewayName",
      header: () => <span className="fp-th">Gateway {roleData[0]?.fiat?.value?.show_fiat_payout_gateway_name ? "Name" : "Hash ID"}</span>,
      cell: (info: any) => (
        !roleData[0]?.fiat?.value?.show_fiat_payout_gateway_name
          ? <ShortTruncateCopy info={info?.row?.original?.gatewayId || "—"} showCopy />
          : <div className="fp-gateway-cell">
              <img style={{ height:"auto", width:"14px" }} src={getImage(info.getValue())} alt="" />
              <span className="fp-gateway-name">{info.getValue() || "—"}</span>
            </div>
      ),
    }),
    columnHelper.accessor("totalAmount", {
      id: "totalAmount",
      header: () => <span className="fp-th">Batch Total</span>,
      cell: (info: any) => <span className="fp-amount">{Number(info.getValue() ?? 0).toFixed(2)}</span>,
    }),
    columnHelper.accessor("type", {
      id: "type",
      header: () => <span className="fp-th">Type</span>,
      cell: (info: any) => <span className="fp-type-tag">{String(info.getValue()).replace('-'," ")}</span>,
    }),
    ...(withAction ? [columnHelper.accessor("id", {
      id: "action",
      header: () => <span className="fp-th">Actions</span>,
      cell: (info: any) => (
        <div className="fp-actions">
          {roleData?.[0]?.fiat?.value?.approve_fiat_batch_file && info?.row?.original?.type === 'require-approve'
            ? <button className="fp-approve-btn" onClick={() => approveBatch(info?.row?.original?.id)}>Approve</button>
            : <span className="fp-muted">—</span>}
        </div>
      ),
      enableSorting: false,
    })] : []),
  ];

  const columns = buildCols(true);
  const columnsNonAction = buildCols(false);

  const table = useReactTable({
    data,
    columns: roleData?.[0]?.fiat?.value?.approve_fiat_batch_file ? columns : columnsNonAction,
    state: { pagination, sorting },
    onPaginationChange: setPagination, onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(),
  });

  const handleValueChange = (e: any) => { setExpandedRow(null); onValueChange(e); };

  const findLastErrorMessage = (obj: any): string | null => {
    const errorKeys = ["errorMessage","message","decline_reason","error_message","failure_message","failure_code"];
    let lastValue: string | null = null;
    const search = (data: any) => {
      if (!data || typeof data !== "object") return;
      for (const key of Object.keys(data)) {
        if (errorKeys.includes(key) && key !== "_content") lastValue = data[key];
        if (typeof data[key] === "object") search(data[key]);
        if (key === "_content" && typeof data[key] === "string") {
          try { const p = JSON.parse(data[key]); if (p.decline_reason) lastValue = p.decline_reason; else for (const k of Object.keys(p)) if (errorKeys.includes(k)) lastValue = p[k]; } catch {}
        }
      }
    };
    search(obj); return lastValue;
  };

  const parsePythonJSON = (str: string): any[] => {
    try {
      return JSON.parse(str.replace(/\bNone\b/g,"null").replace(/\bFalse\b/g,"false").replace(/\bTrue\b/g,"true").replace(/([{,]\s*)'([^']+)'\s*:/g,'$1"$2":').replace(/:\s*'([^']*)'/g,': "$1"'));
    } catch { return []; }
  };

  return (
    <div className="fp-table-card">
      <div className="fp-table-header">
        <div className="fp-table-header-left">
          <div className="fp-table-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
          </div>
          <div>
            <h3 className="fp-table-title">Fiat Payouts</h3>
            <p className="fp-table-sub">Batch payout records</p>
          </div>
        </div>
        <div className="fp-table-header-right">
        <AddBatchFile fetchPayouts={fetchPayouts} />
          <Searchbox onSearch={handleValueChange} />
        </div>
      </div>

      <div className="fp-table-wrap">
        {isLoading ? (
          <div className="fp-loading"><div className="fp-spinner" /><p className="fp-loading-txt">Loading batch payouts…</p></div>
        ) : (
          <table className="fp-table">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th key={h.id} colSpan={h.colSpan}
                      onClick={h.column.getCanSort() ? h.column.getToggleSortingHandler() : undefined}
                      className={`fp-thead-th${h.column.getCanSort() ? " fp-thead-th--sort" : ""}`}
                    >
                      <div className="fp-th-inner">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getCanSort() && (
                          <span className="fp-sort-icon">
                            {h.column.getIsSorted() === "asc" ? <BsArrowUp size={11} /> :
                             h.column.getIsSorted() === "desc" ? <BsArrowDown size={11} /> :
                             <RiArrowUpDownFill size={11} />}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row, ri) => (
                  <React.Fragment key={row.id}>
                    <tr className={`fp-tbody-tr${ri % 2 !== 0 ? " fp-tbody-tr--alt" : ""}`}>
                      {row.getVisibleCells().map((cell, ci) => (
                        <td key={ci} className="fp-tbody-td">{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                      ))}
                    </tr>
                    {expandedRow === (row.original as any).id && (
                      <tr className="fp-expanded-row">
                        <td colSpan={columns.length} className="fp-expanded-cell">
                          <div className="fp-subtable-wrap">
                            <h4 className="fp-subtable-heading">Transactions</h4>
                            {Object.keys((row.original as any).transactions ?? {}).length > 0 ? (
                              <table className="fp-table">
                                <thead>
                                  <tr>
                                    {["Created At","Customer ID","Quote ID","Amount","Status",
                                      ...(roleData?.[0]?.fiat?.value?.view_fiat_payout_response_information ? ["Action"] : [])
                                    ].map((h) => (
                                      <th key={h} className="fp-thead-th"><span className="fp-th">{h}</span></th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {Object.entries((row.original as any).transactions).map(([txId, tx]: [string, any]) => (
                                    <tr key={txId} className="fp-tbody-tr">
                                      <td className="fp-tbody-td"><span className="fp-date">{moment(tx.createdAt).format("YYYY-MM-DD HH:mm:ss")}</span></td>
                                      <td className="fp-tbody-td"><ShortTruncateCopy info={tx.customerId} showCopy /></td>
                                      <td className="fp-tbody-td"><span className="fp-muted">—</span></td>
                                      <td className="fp-tbody-td"><span className="fp-amount">{Number(tx.amount ?? 0).toFixed(2)} {tx.currency}</span></td>
                                      <td className="fp-tbody-td">
                                        <CustomToolTip
                                          content={(() => {
                                            try {
                                              const hist = Array.isArray(tx.paymentHistory) ? tx.paymentHistory : typeof tx.paymentHistory === "string" ? parsePythonJSON(tx.paymentHistory) : [];
                                              if (!hist.length) return <span>No history available.</span>;
                                              return hist.map((h: any, i: number) => (
                                                <Card className="m-2 shadow" key={i}>
                                                  <div className="border border-white p-3 max-w-[60vh]">
                                                    <p>Created at {h?.created_at} by API call from [{h?.client} - {h?.ip}] with status {h?.status}</p>
                                                  </div>
                                                </Card>
                                              ));
                                            } catch { return <span>Error loading history</span>; }
                                          })()}
                                          trigger={<button className="fp-status-btn"><StatusRender status={tx.status} value={tx.status} /></button>}
                                          header="Status History"
                                        />
                                      </td>
                                      {roleData?.[0]?.fiat?.value?.view_fiat_payout_response_information && (
                                        <td className="fp-tbody-td"><InfoModal info={tx.gatewayResponse} /></td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <div className="fp-subtable-empty">No transactions available.</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr><td colSpan={10}>
                  <div className="fp-empty">
                    <div className="fp-empty-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/></svg></div>
                    <p className="fp-empty-title">No batch payouts found</p>
                    <p className="fp-empty-sub">Upload a batch file to get started</p>
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="fp-pagination-wrap">
        <Pagination setPage={setPage} page={page} totalpage={totalpage} currentPage={currentPage} pageSize={pageSize} setPageSize={setPageSize} arraySize={[50, 100, 200]} />
      </div>

      {FP_TABLE_STYLES}
      <style>{`
        .fp-expand-cell { display:inline-flex; align-items:center; gap:7px; background:none; border:none; cursor:pointer; padding:0; font-family:'Plus Jakarta Sans',sans-serif; }
        .fp-expand-icon { width:18px; height:18px; flex-shrink:0; }
        .fp-expand-icon--plus  { color:#2563eb; }
        .fp-expand-icon--minus { color:#dc2626; }
        .fp-num { font-size:12.5px; font-weight:700; color:#0f172a; }
        .fp-type-tag { display:inline-block; padding:3px 9px; border-radius:7px; font-size:11px; font-weight:700; background:#f0f9ff; border:1.5px solid #bae6fd; color:#0284c7; text-transform:capitalize; white-space:nowrap; }
        .fp-muted { font-size:12px; color:#cbd5e1; }
        .fp-approve-btn { display:inline-flex; align-items:center; font-family:'Plus Jakarta Sans',sans-serif; font-size:12px; font-weight:700; color:#fff; padding:6px 14px; border-radius:8px; border:none; background:linear-gradient(135deg,#1d4ed8,#2563eb); cursor:pointer; transition:all .15s; }
        .fp-approve-btn:hover { transform:translateY(-1px); }
        .fp-expanded-row { }
        .fp-expanded-cell { padding:0 !important; border-bottom:2px solid #e2e8f0; }
        .fp-subtable-wrap { background:#f0f7ff; border-left:3px solid #bfdbfe; padding:14px 16px; overflow-x:auto; }
        .fp-subtable-heading { font-size:12px; font-weight:700; color:#1e3a8a; text-transform:uppercase; letter-spacing:.5px; margin-bottom:12px; }
        .fp-subtable-empty { font-size:13px; color:#64748b; font-weight:500; text-align:center; padding:20px; }
      `}</style>
    </div>
  );
}

export default PayoutList;